import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { formatISO } from 'date-fns';
import { BehaviorSubject, debounceTime, delayWhen, map, timer } from 'rxjs';
import SignaturePad from 'signature_pad';

import {
  type ElementFormState,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
  SignatureDetailsOption,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { isSignature, Signature } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { resizeRxUtils, setAlphaToHexColor, useEmitTracker } from '@blaze-shared/shared-util';
import { type Nullish } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

const MAX_SIGNATURE_FONT_SIZE_PX = 64;
const MIN_SIGNATURE_FONT_SIZE_PX = 24;

@UntilDestroy()
@Component({
  selector: 'be-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SignatureComponent
  extends CanvasElementBaseComponent<'signature'>
  implements CanvasElementComponent<'signature'>, OnChanges, OnInit, AfterViewInit, OnDestroy
{
  sanitizer = inject(DomSanitizer);

  @Input() value: Nullish<Signature>;
  @Input() formState?: ElementFormState;

  @ViewChild('inputText') inputText?: ElementRef<HTMLInputElement>;
  @ViewChild('pad') pad?: ElementRef<HTMLCanvasElement>;
  @ViewChild('text') text?: ElementRef<HTMLElement>;

  @HostBinding('style.--b-s-text-color') textColor = 'rgba(55, 55, 55, 1)';
  @HostBinding('style.--b-s-light-text-color') lightTextColor = 'rgba(55, 55, 55, 0.50)';
  @HostBinding('style.--b-s-border-color') borderColor = '#e2e2e2';
  @HostBinding('style.--b-s-accent-color') accentColor = '#118dff';
  @HostBinding('style.--b-s-border-radius') borderRadius = '0';

  private outputEmitTracker = useEmitTracker();

  private wasEmitted = false;
  private isFocusedValue = false;
  private signatureDrawn = (): void => {
    if (!this.signaturePad) {
      return;
    }

    this.hasHandwriting = true;
    this.updateOutput({
      fullName: this.signature$$.value?.fullName,
      signedDate: formatISO(new Date()),
      signature: this.signaturePad.toSVG(),
    });
  };

  wrapperSectionKey!: string;

  labelEnabled!: boolean;
  labelText!: string;

  placeholder!: string;
  isReadonly = false;

  formControl = new UntypedFormControl('');
  signature$$ = new BehaviorSubject<Partial<Signature>>({});
  signaturePad?: SignaturePad;
  hasHandwriting = false;
  fontSizePx$$: BehaviorSubject<{ size: number; grow: boolean }> = new BehaviorSubject({
    size: MAX_SIGNATURE_FONT_SIZE_PX,
    grow: true as boolean,
  });
  fontSize$ = this.fontSizePx$$.pipe(map(({ size }) => `${size}px`));
  controlStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;
  groupStyles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  inputStyles!: Record<string, string>;

  set isFocused(value: boolean) {
    this.isFocusedValue = value;
    if (this.props?.style.input?.border.selectorType !== 'none') {
      const color = value
        ? this.props?.style.general?.accent.color1
        : this.props?.style.input?.border.color1;
      this.inputWrapperStyles['border'] = `1px solid ${color}`;
      this.cdr.detectChanges();
    }
  }
  get isFocused(): boolean {
    return this.isFocusedValue;
  }

  get showErrorLabel(): boolean | undefined {
    return (this.formControl.invalid && this.formControl.dirty) || this.hasValidationError;
  }

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get isHandwritten(): boolean {
    return this.isReadonly
      ? !!this.value?.signature
      : this.props?.content.input?.signatureType === 'Handwritten';
  }

  get detailsType(): SignatureDetailsOption {
    return this.props?.content?.options?.detailsType ?? 'full';
  }

  constructor() {
    super();

    /**
     * The updates to the form control invoke the output update logic
     */
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.updateOutput({
        fullName: value,
        signedDate: value || this.signature$$.value.signature ? formatISO(new Date()) : undefined,
        signature: this.signature$$.value.signature ?? null,
      });
    });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      if (!this.wrapperSectionKey) {
        this.wrapperSectionKey = `section-${this.elementId}`;
      }
      const { title, input } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.placeholder = input?.placeholder || '';
      this.isReadonly = input?.readonly || false;
      if (this.isReadonly) {
        this.signaturePad?.off();
      } else {
        this.signaturePad?.on();
      }

      this.updateStyles();
    }

    if (changes.value) {
      if (isSignature(this.value) || this.value == undefined) {
        // display in details view, but clear on form to force re-sign
        const value = this.isReadonly ? (this.value ?? null) : null;
        this.updateOutput(value ?? {}, true);
        this.formControl.setValue(value?.fullName, { emitEvent: false });
        this.hasHandwriting = false;
        this.signaturePad?.clear();
      }
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        safeDetectChanges(this.cdr);
      });
    this.setDefaultEventSchema('change');
  }

  ngAfterViewInit(): void {
    if (!this.pad) {
      return;
    }
    this.signaturePad = new SignaturePad(this.pad?.nativeElement);
    this.signaturePad.penColor = 'currentColor';
    this.signaturePad.addEventListener('endStroke', this.signatureDrawn);
    if (this.isReadonly) {
      this.signaturePad?.off();
    } else {
      this.signaturePad?.on();
    }

    resizeRxUtils
      .fromResizeObserver(this.pad.nativeElement)
      .pipe(debounceTime(500), untilDestroyed(this))
      .subscribe(() => {
        this.resizeCanvas();
      });

    this.signature$$.pipe(debounceTime(200), untilDestroyed(this)).subscribe(() => {
      this.fontSizePx$$.next({ size: this.fontSizePx$$.value.size, grow: true });
    });
    this.fontSizePx$$
      .pipe(
        delayWhen(() => timer(0)),
        untilDestroyed(this),
      )
      .subscribe(({ size, grow }) => {
        if (
          size > MIN_SIGNATURE_FONT_SIZE_PX &&
          (this.text?.nativeElement.clientWidth ?? Infinity) <
            (this.text?.nativeElement.scrollWidth ?? 0)
        ) {
          this.fontSizePx$$.next({ size: size - 1, grow: false });
        } else if (size < MAX_SIGNATURE_FONT_SIZE_PX && grow) {
          this.fontSizePx$$.next({ size: size + 1, grow: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.signaturePad?.removeEventListener('endStroke', this.signatureDrawn);
    this.signaturePad?.off();
  }

  onAreaClicked(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).id !== this.wrapperSectionKey ||
      this.isReadonly ||
      !this.inputText?.nativeElement
    ) {
      return;
    }

    const editorBody = this.inputText.nativeElement;
    if (!editorBody) {
      return;
    }

    const yDiff = editorBody.offsetTop - event.offsetY;
    if (yDiff > 0) {
      // clicked above
      if (yDiff < 32 && event.offsetX > editorBody.offsetWidth / 2) {
        // but in the right half and close enough to the editor
        this.makeEditorSelection(true);
      } else {
        this.makeEditorSelection();
      }
    } else if (event.offsetY > editorBody.offsetTop + editorBody.offsetHeight) {
      // clicked below
      this.makeEditorSelection(true);
    }
  }

  removeHandwriting(): void {
    if (!this.signaturePad) {
      return;
    }

    this.signaturePad.clear();
    this.hasHandwriting = false;
    if (this.signature$$.value) {
      this.updateOutput({
        fullName: this.signature$$.value?.fullName,
        signedDate: this.signature$$.value?.fullName
          ? this.signature$$.value?.signedDate
          : undefined,
        signature: null,
      });
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, input, inputFont, labelBlock } = style || {};
    if (!style || !title || !general || !input || !inputFont || !labelBlock) {
      return;
    }

    const controlStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};
    const groupStyles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.value.color1,
      ...makeFontStyles(inputFont, { lineHeight: true }),
    };
    const inputStyles: Record<string, string> = {
      ...makeFontStyles(inputFont, { lineHeight: true }),
      ...makeFontStyleStyles(inputFont.style),
      ['font-weight']: this.fontsService.getFontWeight(inputFont.fontFamily, inputFont.fontWeight),
    };

    if (title.enabled !== false) {
      if (title.position === 'top') {
        controlStyles['flex-direction'] = `column`;
      } else {
        validationStyles['padding-left'] = '2px';
        groupStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      this.borderColor = input.border.color1;
    }
    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.controlStyles = controlStyles;
    this.validationStyles = validationStyles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.groupStyles = groupStyles;
    this.inputStyles = inputStyles;
    this.textColor = input.value.color1;
    this.lightTextColor = setAlphaToHexColor(input.value.color1, 50);
    if (this.props?.style.general?.accent.color1) {
      this.accentColor = this.props?.style.general?.accent.color1;
    }
    this.borderRadius = input.radius;
  }

  private updateOutput(data: Partial<Signature>, force = true): void {
    this.signature$$.next(data);
    this.value =
      data.fullName && data.signedDate && (!this.isHandwritten || data.signature)
        ? { fullName: '', signature: null, signedDate: '', userId: null, ...data }
        : null;
    if (force || this.outputEmitTracker.shouldEmit(this.value)) {
      this.output.emit({
        eventType: 'change',
        data: this.value,
        schema: this.getDefaultEventSchema('change'),
        isInitialData: !this.wasEmitted,
      });
      this.wasEmitted = true;
    }
  }

  private makeEditorSelection(goToEnd = false): void {
    const input = this.inputText?.nativeElement;
    if (!input) {
      return;
    }

    const point = goToEnd ? input.value.length : 0;
    input.focus();
    const inputType = input.type;
    input.type = 'text';
    input.selectionStart = point;
    input.selectionEnd = point;
    input.type = inputType;
  }

  private resizeCanvas(): void {
    const canvas = this.pad?.nativeElement;
    if (!canvas) {
      return;
    }
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    this.signaturePad?.redraw();
  }
}
